import { useState } from 'react';
import { ScanLine } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ScanDialog } from './ScanDialog';

/**
 * Scan, from anywhere.
 *
 * The scan form lived only behind Tracking → Scan, so recording a scan meant navigating away from
 * whatever screen you were on and back again. This puts the same dialog in the global header: one
 * click, popup opens, no navigation.
 *
 * The dialog needs an orderType (it decides whether the box asks for an AWB or an LR / child AWB,
 * and which id the results table leads with), and outside the Tracking page there is nothing to
 * infer it from — so the choice is a toggle at the top of the modal. It is remembered for the rest
 * of the session, because an operator scanning a trolley of B2B cartons picks B2B once, not once
 * per parcel. Fresh mount = B2C, which is the larger volume.
 */
export function QuickScanButton() {
  const [open, setOpen] = useState(false);
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>('B2C');

  return (
    <>
      <button onClick={() => setOpen(true)} title="Scan — add a tracking event to one or more AWBs"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 rounded-lg text-white text-xs font-medium transition">
        <ScanLine size={14} /> Scan
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Scan Status"
        subtitle="Add a tracking event to one or more AWBs" maxWidth="5xl">
        <div className="mb-4">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5 block">Shipment Type</label>
          <div className="inline-flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            {(['B2C', 'B2B'] as const).map(t => (
              <button key={t} onClick={() => setOrderType(t)}
                className={`px-4 py-1.5 text-sm rounded-md transition ${orderType === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        {/*
          Keyed on orderType so switching it REMOUNTS the form and clears any rows already added.
          A row scanned as B2C carries B2C lookup metadata (master AWB vs child box), and carrying it
          into a B2B submit would post the wrong identifier for that shipment.
        */}
        <ScanDialog key={orderType} orderType={orderType} onClose={() => setOpen(false)} />
      </Modal>
    </>
  );
}
